//#region react
import React, { useMemo, useState, useEffect } from 'react';

//bootstrap
import { Card, Form, Button } from 'react-bootstrap';

// firestore — for RC session reports listener
import { firestore } from "_utils/firebase-app";
import { collection, onSnapshot, query, where, limit } from "firebase/firestore";

// redux
import { useDispatch, useSelector } from 'react-redux';
import { updateAdhocTaskNotes } from '_services/timeline-service';
import { selectFirebaseUser } from '_redux/slices/main-screen/user';
import { updateTimelineBooking, selectUpdateStatus } from '_redux/slices/timeline/timeline-bookings';
import { updateTask, selectUpdateTaskStatus } from '_redux/slices/timeline/timeline-data';

// styles 
import { Spacing, Colors } from '_styles'
import * as TaskColors from '../utils/colors';

// components
import { CustomSpinner } from '_atoms';

// functions
import { getReadableTimeString, getDurationTime, getHoursAndMinutes, getTaskCardStatus } from '_utils/functions';

import moment from 'moment';
import { selectEmployeeList } from '_redux/slices/timeline/employees';
//#endregion

//#region styles
const CARD_STYLE = {
    marginBottom: Spacing.SCALE_16,
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    backgroundColor: 'white',
};

const ROW_STYLE = {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'flex-start',
    gap: Spacing.SCALE_16,
    flexWrap: 'wrap',
};

const ITEM_STYLE = {
    flex: 1,
    minWidth: '120px',
    textAlign: 'flex-start',
    backgroundColor: 'transparent',
    border: '2px solid transparent',
    borderRadius: '6px',
    padding: 0,
    transition: 'all 0.2s ease',
};

const LABEL_STYLE = {
    fontSize: 12,
    fontWeight: 600,
    color: '#6b7280',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
};

const VALUE_STYLE = {
    fontSize: 14,
    color: '#374151',
    margin: 0,
    fontWeight: 500,
};

const CONTENT_STYLE = { padding: Spacing.SCALE_16 };
//#endregion

//#region custom components
const InfoItem = ({ label, children }) => (
    <div style={ITEM_STYLE}>
        <div style={LABEL_STYLE}>{label}</div>
        <div style={VALUE_STYLE}>{children}</div>
    </div>
);

// Helper to format task type to readable label
const getTaskTypeLabel = (type) => {
    const typeMap = {
        'preboard': 'Preboard',
        'arrival': 'Arrival',
        'ramp-task': 'Ramp',
        'boarding': 'Boarding',
        'alt-pickup': 'Alt Pickup',
        'break-task': 'Break',
        'adhoc-task': 'Adhoc',
        'departure': 'Departure',
        'job': 'Job',
        'gate-relocation': 'Gate Relocation',
    };
    return typeMap[type?.toLowerCase()] || type || 'Not Set';
};
//#endregion

const TaskInformationCard = ({ task, iata }) => {

    const dispatch = useDispatch();
    const updateBookingStatus = useSelector(selectUpdateStatus);
    const updateTaskStatus = useSelector(selectUpdateTaskStatus);
    const firebaseUser = useSelector(selectFirebaseUser);
    const allEmployees = useSelector(selectEmployeeList);
    // Notes state
    const [notes, setNotes] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    // Adhoc task notes state
    const [adhocNotes, setAdhocNotes] = useState([]);
    const [adhocNoteInput, setAdhocNoteInput] = useState('');
    const [adhocNoteSaving, setAdhocNoteSaving] = useState(false);
    const [noteEntries, setNoteEntries] = useState([]);
    const [noteInput, setNoteInput] = useState('');
    const [assignedEmployee, setAssignedEmployee] = useState(null);
    // Determine if this is a task that should be updated directly (ALT pickup, ramp task, boarding task)
    // or if it's a departure/arrival task that should update the original booking
    const isTaskUpdate = task?.isALTPickUp || task?.combinedRampBooking || task?.combinedBooking || task?._id?.includes('_BOARDING_');

    // Get the original booking ID for departure/arrival tasks
    const getOriginalBookingId = () => {
        // For arrival tasks, use originalBookingId
        if (task?.originalBookingId) {
            return task.originalBookingId;
        }
        // For departure tasks, remove _IATA_XXX suffix from ID
        if (task?._id?.includes('_IATA_')) {
            return task._id.split('_IATA_')[0];
        }
        return task?._id;
    };

    const bookingId = getOriginalBookingId();
    const isLoading = isTaskUpdate ? updateTaskStatus === 'loading' : updateBookingStatus === 'loading';

    // Initialize notes from task/booking
    useEffect(() => {
        if (task) {
            if (task?.isAdhocTask) {
                // Adhoc tasks store notes as array of {note, timestamp}
                setAdhocNotes(Array.isArray(task.notes) ? task.notes : []);
                setNotes('');
            } else {
                setNoteEntries(normalizeTaskNotes(task?.taskNotes?.length ? task.taskNotes : task?.notes));
            }

            setNoteInput('');
            setIsEditing(false);
        }
    }, [task]);

    useEffect(() => {
        if (task && Array.isArray(allEmployees)) {
            const assignedEmp = allEmployees.find(emp => emp.employeeId === task.employeeId);
            setAssignedEmployee(assignedEmp ? assignedEmp.title : null);
        }
    }, [task]);

    const normalizeTaskNotes = (value) => {
        const normalizeText = (input) => {
            if (typeof input === 'string') {
                const trimmed = input.trim();
                return trimmed || null;
            }

            if (Array.isArray(input)) {
                const flattened = input
                    .map((item) => {
                        if (typeof item === 'string') return item.trim();
                        if (item && typeof item === 'object') return normalizeText(item.text ?? item.note);
                        return '';
                    })
                    .filter(Boolean);

                return flattened.length ? flattened.join('. ') : null;
            }

            if (input && typeof input === 'object') {
                return normalizeText(input.text ?? input.note);
            }

            return null;
        };
        const normalizeEntry = (note) => {
            if (typeof note === 'string') {
                const text = normalizeText(note);
                return text ? { text } : null;
            }

            if (!note || typeof note !== 'object') return null;

            const text = normalizeText(note.text ?? note.note);
            if (!text) return null;

            return {
                ...note,
                text,
                time: note.time || note.timestamp || null,
                userName: note.userName || note.author || null,
            };
        };

        if (Array.isArray(value)) {
            return value.map(normalizeEntry).filter(Boolean);
        }

        if (typeof value === 'string' && value.trim()) {
            return [{ text: value.trim() }];
        }

        if (value && typeof value === 'object') {
            const normalized = normalizeEntry(value);
            return normalized ? [normalized] : [];
        }

        return [];

    }

    // Handle save notes - updates the task or the original booking depending on type
    const handleSaveNotes = () => {
        const trimmedNote = noteInput.trim();
        if (!trimmedNote) return;
        // Create a new taskNotes entry
        const newTaskNote = {
            time: new Date().toISOString(),
            text: notes,
            text: trimmedNote,
            userId: firebaseUser?.uid || firebaseUser?.id || "unknown",
            userName: firebaseUser?.displayName || firebaseUser?.name || firebaseUser?.email || "Unknown",
        };
        const updatedTaskNotes = [...noteEntries, newTaskNote];
        if (isTaskUpdate) {
            // Update the task directly (ALT pickup, ramp task, boarding task)
            if (!task?._id) return;
            dispatch(updateTask({
                id: task._id,
                notes: updatedTaskNotes,
                taskNotes: updatedTaskNotes,
            }));
        } else {
            // For departure/arrival tasks, update the original booking
            // Format: { id: bookingId, body: { ...fieldsToUpdate } }
            if (!bookingId) return;
            dispatch(updateTimelineBooking({
                id: bookingId,
                body: { notes: updatedTaskNotes }
            }));

            // For departure tasks (have _IATA_ in ID), also update the task document
            if (task?._id?.includes('_IATA_')) {
                dispatch(updateTask({
                    id: task._id,
                    notes: updatedTaskNotes,
                    taskNotes: updatedTaskNotes,
                }));
            }
        }
        setNoteEntries(updatedTaskNotes);
        setNoteInput('');
        setSaveSuccess(true);
        setIsEditing(false);

        // Clear success message after 2 seconds
        setTimeout(() => setSaveSuccess(false), 2000);
    };

    // Handle save notes for adhoc tasks — direct Firestore write
    const handleSaveAdhocNote = async () => {

        if (!adhocNoteInput.trim() || !task?._id) return;

        const newNote = {
            note: adhocNoteInput.trim(),
            timestamp: new Date().toISOString(),
        };
        const updatedNotes = [...adhocNotes, newNote];
        setAdhocNoteSaving(true);
        try {
            await updateAdhocTaskNotes(task._id, updatedNotes);
            setAdhocNotes(updatedNotes);
            setAdhocNoteInput('');
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 2000);
        } catch (e) {
            console.error('Error saving adhoc note:', e);
        } finally {
            setAdhocNoteSaving(false);
        }
    };

    // Derive task type from task properties
    const type = useMemo(() => {
        if (task?.isGateRelocation) return 'gate-relocation';
        if (task?.isBreakTask) return 'break-task';
        if (task?.isAdhocTask) return 'adhoc-task';
        if (task?.isALTPickUp) return 'alt-pickup';
        if (task?.combinedRampBooking) return 'ramp-task';
        if (task?.combinedBooking || task?.isBoardingBooking) return 'boarding';
        if (iata === task?.flightInformation?.arrivalAirport) return 'arrival';
        if (iata === task?.flightInformation?.departureAirport) return 'departure';
        return 'job';
    }, [task, iata]);

    const isBreakTask = type === "break-task";
    const isAdhocTask = type === "adhoc-task";
    const taskTypeLabel = getTaskTypeLabel(type);

    // Get flight number from task
    const flightNumber = task?.flightInformation?.flightNumber
        || (task?.flightConfirmationDetails?.carrierCode + task?.flightConfirmationDetails?.flightNumber)
        || '';

    // Derive all time values from task
    const allocatedTime = task?.allocatedTime ? getReadableTimeString(task.allocatedTime) : 'Not Set';
    const acknowledgedTime = task?.acknowledgedTime ? getReadableTimeString(task.acknowledgedTime) : 'Not Set';
    const atLocationTime = task?.atLocationTime ? getReadableTimeString(task.atLocationTime) : 'Not Set';

    // Derive the effective start time. Rejected/disregarded tasks (arrival, marshall,
    // ramp) frequently complete without a real `startTime`. Falling back to
    // acknowledgedTime rendered a start earlier than atLocationTime, breaking the
    // confirm → at location → start → complete sequence. Prefer the real startTime,
    // then atLocationTime (when work actually began at the stand/gate), and only then
    // the confirmed time — never default to `now`.
    const startTimeSource = task?.startTime || task?.atLocationTime || (task?.completed ? task?.acknowledgedTime : null);
    const startTime = startTimeSource ? getReadableTimeString(startTimeSource) : 'Not Started';
    const completeTime = task?.completeTime ? getReadableTimeString(task.completeTime) : 'Not Completed';
    const pickupTime = task?.pickUpTime ? getReadableTimeString(task.pickUpTime) : 'Not Picked';

    // Task duration — measured from the effective start so it can never be negative.
    const taskDuration = (startTimeSource && task?.completeTime)
        ? getDurationTime(startTimeSource, task.completeTime)
        : 'Not Completed';

    // Break task specific info
    const assignedTime = task?.assignedTime || '';
    const startBreakTaskTime = task?.breakTaskInformation?.breakStartTime
        ? getHoursAndMinutes(task.breakTaskInformation.breakStartTime)
        : '';
    const endBreakTaskTime = task?.breakTaskInformation?.breakEndTime
        ? getHoursAndMinutes(task.breakTaskInformation.breakEndTime)
        : '';
    const breakTaskPeriod = task?.breakTaskInformation?.breakTaskPeriod || '';

    // At location information
    const atLocationInformation = useMemo(() => {
        if (task?.atLocationBeaconVerified) {
            return "Beacon Verified: " + task?.atLocationInformation?.timestamp?.split('T')[1];
        } else if (task?.atLocationQrVerified) {
            return "QR Verified: " + task?.atLocationInformation?.timestamp?.split('T')[1];
        }
        return 'N/A';
    }, [task]);

    // Task status
    const { status: taskStatus } = getTaskCardStatus(task);

    // Get background color based on task type (similar to card-bottom.js)
    const headerBackgroundColor = useMemo(() => {
        // Gate relocation
        if (task?.isGateRelocation) return TaskColors.gateRelocationTask;

        // Break task
        if (task?.isBreakTask) return Colors.ORANGE;

        // Adhoc task
        if (task?.isAdhocTask) return Colors.SUNFLOWER_YELLOW;

        // ALT Pickup
        if (task?.isALTPickUp) return 'purple';

        // Ramp task
        if (type === "ramp-task" || task?.combinedRampBooking) {
            return TaskColors.combinedRampBooking;
        }

        // Boarding task
        if (type === "boarding" || task?.combinedBooking || task?.isBoardingBooking) {
            return TaskColors.combinedBooking;
        }

        // Arrival task
        if (iata === task?.flightConfirmationDetails?.destination ||
            iata === task?.flightInformation?.arrivalAirport) {
            return Colors.ARRIVALS_GREEN;
        }

        // Departure task
        if (iata === task?.flightConfirmationDetails?.origin ||
            iata === task?.flightInformation?.departureAirport) {
            return Colors.PRM_LIGHT_BLUE;
        }

        return '#8b5cf6'; // Default purple
    }, [task, type, iata]);

    const formatNoteTime = (timestamp) => {
        if (!timestamp) return '';
        return moment(new Date(timestamp)).format("DD/MM/YYYY HH:mm") || '';
    };

    // RC session reports listener 
    // For remote_checkin tasks, watch the remotecheckin_sessions document by
    // bookingId so reports surface on the portal card in real time regardless
    // of whether a standard task has been allocated yet.
    const [rcReports, setRcReports] = useState([]);

    useEffect(() => {

        if (!bookingId) return;

        const q = query(
            collection(firestore, 'remotecheckin_sessions'),
            where('bookingId', '==', bookingId),
            limit(1)
        );

        const unsub = onSnapshot(q, (snap) => {
            if (snap.empty) return;
            const data = snap.docs[0].data();
            setRcReports(Array.isArray(data.reports) ? data.reports : []);
        });

        return () => unsub();
    }, [bookingId]);
    return (
        <>
            {/* Task Information Card */}
            <Card style={CARD_STYLE}>
                <Card.Header style={{ backgroundColor: headerBackgroundColor, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <p style={{ margin: 0, color: isAdhocTask ? '#333' : 'white', fontSize: 16, fontWeight: 600 }}>
                        {isBreakTask ? 'Break Task'
                            : isAdhocTask ? `Adhoc — ${task?.adhocTaskInformation?.title || ''}`
                                : `${flightNumber} ${taskTypeLabel} Task`}
                    </p>
                    {taskStatus && !isBreakTask && (
                        <span style={{
                            backgroundColor: taskStatus === 'Completed' ? '#22c55e' : taskStatus === 'Active' ? '#f59e0b' : '#6b7280',
                            color: 'white',
                            fontSize: 11,
                            fontWeight: 'bold',
                            padding: '2px 8px',
                            borderRadius: 12
                        }}>
                            {taskStatus?.toUpperCase()}
                        </span>
                    )}
                </Card.Header>
                <Card.Body style={CONTENT_STYLE}>
                    {isAdhocTask ? (
                        <>
                            <div style={ROW_STYLE}>
                                <InfoItem label="Task Type">{task?.adhocTaskInformation?.title || 'N/A'}</InfoItem>
                                <InfoItem label="Assigned Time">{task?.assignedTime || 'N/A'}</InfoItem>
                                <InfoItem label="Status">{taskStatus || 'N/A'}</InfoItem>
                            </div>

                            {task?.adhocTaskInformation?.description && (
                                <div style={{ ...ROW_STYLE, marginTop: Spacing.SCALE_16 }}>
                                    <InfoItem label="Description">{task.adhocTaskInformation.description}</InfoItem>
                                </div>
                            )}

                            <div style={{ ...ROW_STYLE, marginTop: Spacing.SCALE_16 }}>
                                <InfoItem label="Allocated Time">{allocatedTime}</InfoItem>
                                <InfoItem label="Start Time">{startTime}</InfoItem>
                                <InfoItem label="Complete Time">{completeTime}</InfoItem>
                            </div>

                            {(task?.startTime && task?.completeTime) && (
                                <div style={{ ...ROW_STYLE, marginTop: Spacing.SCALE_16 }}>
                                    <InfoItem label="Task Duration">{taskDuration}</InfoItem>
                                    <InfoItem label="">{""}</InfoItem>
                                    <InfoItem label="">{""}</InfoItem>
                                </div>
                            )}
                        </>
                    ) : !isBreakTask ? (
                        <>
                            {/* Passenger Name Row - only show if available */}
                            {(task?.passengerInformation?.title || task?.name || task?.passengers?.length > 0) && (
                                <div style={{ ...ROW_STYLE, marginBottom: Spacing.SCALE_16, paddingBottom: Spacing.SCALE_16, borderBottom: '1px solid #e5e7eb' }}>
                                    <InfoItem label="Passenger">
                                        {task?.passengers?.length > 0
                                            ? task.passengers.map(p => p.name).join(", ")
                                            : task?.passengerInformation?.title || task?.name || 'N/A'
                                        }
                                    </InfoItem>
                                    {task?.passengerInformation?.assistance?.length > 0 && (
                                        <InfoItem label="Assistance">
                                            {task.passengerInformation.assistance.join(", ")}
                                        </InfoItem>
                                    )}
                                    {assignedEmployee ? <InfoItem label="Assigned Employee">{assignedEmployee}</InfoItem> : <InfoItem label="">{""}</InfoItem>}
                                </div>
                            )}

                            {/* Task Info Row */}
                            <div style={ROW_STYLE}>
                                <InfoItem label="Pickup">{task?.pickUpLocation?.name ?? 'Not Set'}</InfoItem>
                                <InfoItem label="Drop Off">{task?.dropOffLocation?.name ?? 'Not Set'}</InfoItem>
                                {task?.isGateRelocation && task?.relocationReason ? (
                                    <InfoItem label="Reason">{task.relocationReason}</InfoItem>
                                ) : (
                                    <InfoItem label="">{""}</InfoItem>
                                )}
                            </div>

                            {/* Timestamps Row 1 */}
                            <div style={{ ...ROW_STYLE, marginTop: Spacing.SCALE_16 }}>
                                <InfoItem label="Allocated Time">{allocatedTime}</InfoItem>
                                <InfoItem label="Confirmed Time">{acknowledgedTime}</InfoItem>
                                <InfoItem label="At Location Time">{atLocationTime}</InfoItem>
                                {type === 'alt-pickup' && <InfoItem label="Pickup Time">{pickupTime}</InfoItem>}
                            </div>

                            {/* Timestamps Row 2 */}
                            <div style={{ ...ROW_STYLE, marginTop: Spacing.SCALE_16 }}>
                                <InfoItem label="Start Time">{startTime}</InfoItem>
                                <InfoItem label="Complete Time">{completeTime}</InfoItem>
                                <InfoItem label="Task Duration">{taskDuration}</InfoItem>
                            </div>

                            {/* At Location Info */}
                            {atLocationInformation && atLocationInformation !== 'N/A' && (
                                <div style={{ ...ROW_STYLE, marginTop: Spacing.SCALE_16 }}>
                                    <InfoItem label="At Location Info">{atLocationInformation}</InfoItem>
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            {/* Break Task Info */}
                            <div style={ROW_STYLE}>
                                <InfoItem label="Start Break">{startBreakTaskTime || 'N/A'}</InfoItem>
                                <InfoItem label="End Break">{endBreakTaskTime || 'N/A'}</InfoItem>
                                <InfoItem label="Break Duration">{breakTaskPeriod || 'N/A'}</InfoItem>
                                <InfoItem label="">{""}</InfoItem>
                            </div>
                        </>
                    )}
                </Card.Body>
            </Card>

            {/* RC Passenger Reports Card */}
            {rcReports.length > 0 && (
                <Card style={{ ...CARD_STYLE, border: '1px solid #fca5a5' }}>
                    <Card.Header style={{ backgroundColor: '#ef4444', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <p style={{ margin: 0, color: 'white', fontSize: 16, fontWeight: 600 }}>
                            Passenger Reports
                        </p>
                        <span style={{ backgroundColor: 'white', color: '#ef4444', fontSize: 11, fontWeight: 'bold', padding: '2px 8px', borderRadius: 12 }}>
                            {rcReports.length} / 3
                        </span>
                    </Card.Header>
                    <Card.Body style={CONTENT_STYLE}>
                        <div style={{ display: 'grid', gap: Spacing.SCALE_8 }}>
                            {rcReports.map((report, i) => (
                                <div key={i} style={{
                                    padding: Spacing.SCALE_8,
                                    backgroundColor: '#fef2f2',
                                    borderRadius: 6,
                                    borderLeft: '3px solid #ef4444',
                                }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                            {report.option?.replace(/_/g, ' ') || 'Issue'}
                                        </span>
                                        <span style={{ fontSize: 11, color: '#6b7280' }}>
                                            {report.reportedAt
                                                ? moment(report.reportedAt?.toDate?.() ?? report.reportedAt).format('DD/MM/YYYY HH:mm')
                                                : ''}
                                        </span>
                                    </div>
                                    {report.note && (
                                        <p style={{ margin: 0, fontSize: 13, color: '#374151' }}>{report.note}</p>
                                    )}
                                </div>
                            ))}
                        </div>
                    </Card.Body>
                </Card>
            )}

            {/* Task Notes Card */}
            {!isBreakTask && (
                <Card style={CARD_STYLE}>
                    <Card.Header style={{ backgroundColor: '#6b7280', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <p style={{ margin: 0, color: 'white', fontSize: 16, fontWeight: 600 }}>
                            Task Notes
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {(isLoading || adhocNoteSaving) && <CustomSpinner style={{ color: 'white' }} />}
                            {saveSuccess && (
                                <span style={{ color: '#22c55e', fontSize: 12, fontWeight: 'bold' }}>
                                    ✓ Saved
                                </span>
                            )}
                        </div>
                    </Card.Header>
                    <Card.Body style={CONTENT_STYLE}>
                        {isAdhocTask ? (
                            <>
                                {/* Existing adhoc notes */}
                                {adhocNotes.length > 0 && (
                                    <div style={{ marginBottom: Spacing.SCALE_12 }}>
                                        {adhocNotes.map((n, i) => (
                                            <div key={i} style={{
                                                padding: Spacing.SCALE_8,
                                                marginBottom: Spacing.SCALE_4,
                                                backgroundColor: '#f9fafb',
                                                borderRadius: 6,
                                                borderLeft: '3px solid #dbdb12',
                                            }}>
                                                <p style={{ margin: 0, fontSize: 11, color: '#6b7280', marginBottom: 2 }}>
                                                    {n.timestamp ? new Date(n.timestamp).toLocaleString() : ''}
                                                </p>
                                                <p style={{ margin: 0, fontSize: 14, color: '#374151' }}>{n.note}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* New note input ,only if task not completed*/}
                                <> <Form.Control
                                    as="textarea"
                                    rows={3}
                                    value={adhocNoteInput}
                                    onChange={(e) => setAdhocNoteInput(e.target.value)}
                                    placeholder="Add a new note..."
                                    style={{ fontSize: 14, borderColor: '#e5e7eb', borderRadius: 6, resize: 'vertical' }}
                                />
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleSaveAdhocNote}
                                        disabled={!adhocNoteInput.trim() || adhocNoteSaving}
                                        style={{ marginTop: Spacing.SCALE_8 }}
                                    >
                                        Add Note
                                    </Button></>
                            </>
                        ) : isEditing ? (
                            <>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    value={noteInput}
                                    onChange={(e) => setNoteInput(e.target.value)}
                                    placeholder="Add notes for this task..."
                                    style={{
                                        fontSize: 14,
                                        borderColor: '#e5e7eb',
                                        borderRadius: 6,
                                        resize: 'vertical',
                                    }}
                                />
                                <div style={{ display: 'flex', gap: 8, marginTop: Spacing.SCALE_12 }}>
                                    <Button
                                        variant="primary"
                                        size="sm"
                                        onClick={handleSaveNotes}
                                        disabled={isLoading || !noteInput.trim()}
                                    >
                                        Save Notes
                                    </Button>
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={() => {
                                            setIsEditing(false);
                                            setNoteInput('');
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </>
                        ) : (
                            <div
                                onClick={() => setIsEditing(true)}
                                style={{
                                    cursor: 'pointer',
                                    padding: Spacing.SCALE_12,
                                    backgroundColor: '#f9fafb',
                                    borderRadius: 6,
                                    border: '1px dashed #d1d5db',
                                    minHeight: 80,
                                    transition: 'all 0.2s ease',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                                    e.currentTarget.style.borderColor = '#9ca3af';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = '#f9fafb';
                                    e.currentTarget.style.borderColor = '#d1d5db';
                                }}
                            >
                                {noteEntries.length > 0 ? (
                                    <div style={{ display: 'grid', gap: Spacing.SCALE_8 }}>
                                        {noteEntries.map((note, index) => (
                                            <div
                                                key={`${note?.time || note?.timestamp || 'task-note'}-${index}`}
                                                style={{
                                                    paddingBottom: index === noteEntries.length - 1 ? 0 : Spacing.SCALE_8,
                                                    borderBottom: index === noteEntries.length - 1 ? 'none' : '1px solid #e5e7eb',
                                                }}
                                            >
                                                <p style={{ margin: 0, fontSize: 14, color: '#374151', whiteSpace: 'pre-wrap' }}>
                                                    {note?.text}
                                                </p>
                                                <p style={{ margin: '4px 0 0 0', fontSize: 12, color: '#6b7280' }}>
                                                    {note?.userName || note?.author || 'Unknown'}
                                                    {formatNoteTime(note?.time || note?.timestamp) ? ` - ${formatNoteTime(note?.time || note?.timestamp)}` : ''}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p style={{ margin: 0, fontSize: 14, color: '#9ca3af', fontStyle: 'italic' }}>
                                        Click to add notes...
                                    </p>
                                )}
                            </div>
                        )}
                    </Card.Body>
                </Card>
            )}
        </>
    );
};

export default TaskInformationCard;
